import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { Event } from 'dashboard/models/event';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { User } from 'dashboard/models/user';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import type { StepPreview } from 'lib/interfaces/StepsConfig';

export type RunStepConfig = {
  token: string;
  prevToken: string;
  timestampToken: string;
  chosenIndex?: number;
  isTransitionBack?: boolean;
};

export type RunStepTreeConfig = {
  token: string;
  prevToken: string;
  chosenPathMap?: Record<number, RunStepTreeConfig[]>;
};

export type InquiryRunActors = {
  users: User[];
  workflows: Workflow[];
  workflowRuns: WorkflowRun[];
};

export interface InquiryRunState {
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[];
  workflowVersionMap: WorkflowVersionMap;
  inquiryRunEvents: Event[];
  inquiryRunActors: InquiryRunActors;
  runStepConfigs: RunStepConfig[];
}

export const initialState: InquiryRunState = {
  inquiryTemplateVersion: undefined,
  stepsPreview: [],
  workflowVersionMap: {},
  inquiryRunEvents: [],
  inquiryRunActors: {
    users: [],
    workflows: [],
    workflowRuns: [],
  },
  runStepConfigs: [],
};

interface SetInquiryTemplateVersionPayload {
  inquiryTemplateVersion: InquiryTemplateVersion;
}

interface SetStepsPreviewPayload {
  stepsPreview: StepPreview[];
}

interface SetWorkflowVersionMapPayload {
  workflowVersionMap: WorkflowVersionMap;
}

interface SetInquiryRunEventsPayload {
  inquiryRunEvents: Event[];
}

interface SetInquiryRunActorsPayload {
  inquiryRunActors: InquiryRunActors;
}

interface SetRunStepConfigsPayload {
  runStepConfigs: RunStepConfig[];
}

const { reducer, actions } = createSlice({
  name: 'inquiry-run',
  initialState,
  reducers: {
    setInquiryTemplateVersion: (state, action: PayloadAction<SetInquiryTemplateVersionPayload>) => {
      state.inquiryTemplateVersion = action.payload.inquiryTemplateVersion;
    },
    setStepsPreview: (state, action: PayloadAction<SetStepsPreviewPayload>) => {
      state.stepsPreview = action.payload.stepsPreview;
    },
    setWorkflowVersionMap: (state, action: PayloadAction<SetWorkflowVersionMapPayload>) => {
      state.workflowVersionMap = action.payload.workflowVersionMap;
    },
    setInquiryRunEvents: (state, action: PayloadAction<SetInquiryRunEventsPayload>) => {
      state.inquiryRunEvents = castDraft(action.payload.inquiryRunEvents);
    },
    setInquiryRunActors: (state, action: PayloadAction<SetInquiryRunActorsPayload>) => {
      state.inquiryRunActors = action.payload.inquiryRunActors;
    },
    setRunStepConfigs: (state, action: PayloadAction<SetRunStepConfigsPayload>) => {
      state.runStepConfigs = action.payload.runStepConfigs;
    },
  },
});

export const {
  setInquiryTemplateVersion,
  setStepsPreview,
  setWorkflowVersionMap,
  setInquiryRunEvents,
  setInquiryRunActors,
  setRunStepConfigs,
} = actions;
export const inquiryRunReducer = reducer;
